/**
 * Function reference for the computed-tags editor — categorised list of
 * operators and math functions, used to render the chip palette under
 * the expression textarea. Single source of truth so the catalog stays
 * in sync with what the engine actually supports.
 *
 * `preview: true` flags stateful functions whose semantics are
 * client-side only in Phase 0 and will be validated against Arun's
 * server-side window-buffer worker in Phase 1.
 */

export interface FunctionEntry {
  /** Display chip text — also the snippet inserted into the expression. */
  chip: string;
  /** Optional longer-form description shown on hover. */
  hint?: string;
  /** Phase-0 client-side stateful function. */
  preview?: boolean;
}

export interface FunctionGroup {
  label: string;
  entries: FunctionEntry[];
}

export const FUNCTION_GROUPS: FunctionGroup[] = [
  {
    label: "Arithmetic",
    entries: [
      { chip: "+", hint: "Addition" },
      { chip: "-", hint: "Subtraction / unary negate" },
      { chip: "*", hint: "Multiplication" },
      { chip: "/", hint: "Division" },
      { chip: "%", hint: "Modulo" },
      { chip: "^", hint: "Exponentiation (x ^ 2 = x squared)" },
      { chip: "()", hint: "Grouping — control precedence" },
    ],
  },
  {
    label: "Math",
    entries: [
      { chip: "abs(x)", hint: "Absolute value" },
      { chip: "sqrt(x)", hint: "Square root" },
      { chip: "exp(x)", hint: "Natural exponential" },
      { chip: "log(x)", hint: "Natural log (base e)" },
      { chip: "log10(x)", hint: "Base-10 log" },
      { chip: "min(a, b)", hint: "Smaller of two values" },
      { chip: "max(a, b)", hint: "Larger of two values" },
      { chip: "round(x, n)", hint: "Round to n decimal places" },
      { chip: "floor(x)" },
      { chip: "ceil(x)" },
    ],
  },
  {
    label: "Trig",
    entries: [
      { chip: "sin(x)" },
      { chip: "cos(x)" },
      { chip: "tan(x)" },
      { chip: "asin(x)" },
      { chip: "acos(x)" },
      { chip: "atan(x)" },
      { chip: "atan2(y, x)" },
    ],
  },
  {
    label: "Conditional",
    entries: [
      { chip: "x > 0 ? a : b", hint: "Ternary if-then-else" },
      { chip: "and / or / not", hint: "Boolean logic" },
      { chip: "== / != / <= / >=", hint: "Comparisons" },
    ],
  },
  {
    label: "Stateful (Preview · Phase 1)",
    entries: [
      {
        chip: "ema(x, period)",
        hint: "Exponential moving average over period samples — smooths chatter.",
        preview: true,
      },
      {
        chip: "rolling_mean(x, n)",
        hint: "Simple moving average over the last N samples.",
        preview: true,
      },
      {
        chip: "lag(x, n)",
        hint: "Value N samples ago — useful for lag features in time-series models.",
        preview: true,
      },
      {
        chip: "stddev_n(x, n)",
        hint: "Sample standard deviation over the last N samples — anomaly z-scores etc.",
        preview: true,
      },
    ],
  },
];
